import type { HttpCore } from '../http';
import type { FetchLike } from '../types';

/**
 * The learner's personal reading state — annotations, progress, read
 * sections — as opposed to chapter CONTENT (`modules/courses.ts`). Types
 * mirror `frontend/web-ide/src/services/reading.ts`.
 *
 * Every call here is best-effort in the source file: these backends were
 * built in parallel with the reader, so a 404/network error degrades
 * gracefully (empty list, no-op) rather than breaking reading. Preserved
 * here exactly.
 */

export type HighlightColor = 'yellow' | 'green' | 'pink' | 'violet';

export interface ReadingNote {
  id: string;
  sectionId: string;
  anchor: string;
  body: string;
  createdAt: string;
}

export interface ReadingHighlight {
  id: string;
  anchor: string;
  quote: string;
  color: HighlightColor;
  createdAt: string;
}

export interface Annotations {
  notes: ReadingNote[];
  highlights: ReadingHighlight[];
}

/** Map of chapterId -> the sectionIds the learner has already read. */
export type ReadSectionsMap = Record<string, string[]>;

export function createReadingModule(core: HttpCore, fetchImpl: FetchLike) {
  return {
    getAnnotations: async (courseId: string, chapterId: string): Promise<Annotations> => {
      try {
        const data = await core.request<{ notes?: unknown[]; highlights?: unknown[] }>(
          `/portal/reading/${encodeURIComponent(courseId)}/${encodeURIComponent(chapterId)}/annotations`,
        );
        return {
          notes: Array.isArray(data?.notes) ? (data.notes as ReadingNote[]) : [],
          highlights: Array.isArray(data?.highlights) ? (data.highlights as ReadingHighlight[]) : [],
        };
      } catch {
        return { notes: [], highlights: [] };
      }
    },

    createNote: (courseId: string, chapterId: string, input: { sectionId: string; anchor: string; body: string }) =>
      core.request<ReadingNote>(
        `/portal/reading/${encodeURIComponent(courseId)}/${encodeURIComponent(chapterId)}/notes`,
        { method: 'POST', body: input },
      ),

    updateNote: (id: string, body: string) =>
      core.request<ReadingNote>(`/portal/reading/notes/${encodeURIComponent(id)}`, { method: 'PATCH', body: { body } }),

    deleteNote: (id: string) =>
      core.request<void>(`/portal/reading/notes/${encodeURIComponent(id)}`, { method: 'DELETE' }),

    createHighlight: (
      courseId: string,
      chapterId: string,
      input: { anchor: string; quote: string; color: HighlightColor },
    ) =>
      core.request<ReadingHighlight>(
        `/portal/reading/${encodeURIComponent(courseId)}/${encodeURIComponent(chapterId)}/highlights`,
        { method: 'POST', body: input },
      ),

    deleteHighlight: (id: string) =>
      core.request<void>(`/portal/reading/highlights/${encodeURIComponent(id)}`, { method: 'DELETE' }),

    /** Fire-and-forget on the caller's scroll tick; the server latches completion once `completed` is true. */
    postProgress: (courseId: string, chapterId: string, scrollPercent: number, completed: boolean) =>
      core
        .request<void>(`/portal/reading/${encodeURIComponent(courseId)}/${encodeURIComponent(chapterId)}/progress`, {
          method: 'POST',
          body: { scrollPercent, completed },
        })
        .then(() => undefined),

    /** Beacon once-per-section; swallows any failure so tracking never disrupts reading. */
    postSectionRead: (courseId: string, chapterId: string, sectionId: string) =>
      core
        .request<void>(`/portal/reading/${encodeURIComponent(courseId)}/${encodeURIComponent(chapterId)}/section`, {
          method: 'POST',
          body: { sectionId },
        })
        .then(() => undefined)
        .catch(() => undefined),

    getReadSections: async (courseId: string): Promise<ReadSectionsMap> => {
      try {
        const data = await core.request<{ chapters?: Record<string, unknown> }>(
          `/portal/reading/${encodeURIComponent(courseId)}/read-sections`,
        );
        const raw = data?.chapters;
        if (!raw || typeof raw !== 'object') return {};
        const out: ReadSectionsMap = {};
        for (const [chapterId, ids] of Object.entries(raw)) {
          if (Array.isArray(ids)) out[chapterId] = ids.filter((s): s is string => typeof s === 'string');
        }
        return out;
      } catch {
        return {};
      }
    },

    /**
     * WebVTT karaoke-caption sidecar for a chapter's audiobook. The audio
     * signed URL is stem-signed so the same token authorizes the sibling
     * `.vtt` — swap the extension on the already-signed URL. Plain GET, no
     * auth header (the URL itself is the credential), so this bypasses
     * `core.request` and uses the injected `fetch` directly.
     */
    fetchCaptions: async (signedAudioUrl: string): Promise<string | null> => {
      if (!signedAudioUrl) return null;
      const vttUrl = signedAudioUrl.replace(/\.mp3(?=$|\?)/, '.vtt');
      if (vttUrl === signedAudioUrl) return null;
      try {
        const res = await fetchImpl(vttUrl);
        if (!res.ok) return null;
        return await res.text();
      } catch {
        return null;
      }
    },
  };
}

export type ReadingModule = ReturnType<typeof createReadingModule>;
